// Generalized pagination bar: results summary, per-page selector, and numbered
// pages with prev/next. Purely presentational — the parent owns page/pageSize
// state and slices its own list. Designed to sit both above and below a grid.

import { useEffect, useMemo, useState } from "react";

import Dropdown from "@/components/Dropdown";

export const PAGE_SIZE_OPTIONS = [10, 20, 50];

// Owns page/pageSize state and returns the current slice. `items` should be a
// stable reference (memoized list or state array) — the page resets to 1
// whenever that reference or the page size changes, so filtering/searching in
// the parent naturally sends the user back to page one.
export function usePagination<T>(items: T[], initialPageSize: number = PAGE_SIZE_OPTIONS[1]) {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  useEffect(() => {
    setPage(1);
  }, [items, pageSize]);

  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const paged = useMemo(
    () => items.slice((safePage - 1) * pageSize, safePage * pageSize),
    [items, safePage, pageSize],
  );

  return { page: safePage, pageSize, setPage, setPageSize, paged, total: items.length };
}

type PaginationProps = {
  page: number; // 1-indexed current page
  pageSize: number;
  total: number; // total item count across all pages
  onPage: (page: number) => void;
  onPageSize?: (size: number) => void; // when provided, renders the per-page selector
  pageSizeOptions?: number[];
  showSummary?: boolean; // "1–20 of 137"
  showNav?: boolean; // the numbered prev/next control (default true)
  showSteps?: boolean; // icon-only ‹ / › buttons flanking the summary
  stepLabels?: boolean; // render the step buttons as "Prev"/"Next" text instead of bare ‹ / › icons
};

// First + last always shown; a sliding window of ±1 around the current page in
// between, with "…" filling any gap. Keeps the control to a fixed width.
function pageItems(page: number, pageCount: number): (number | "ellipsis")[] {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const items: (number | "ellipsis")[] = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(pageCount - 1, page + 1);
  if (start > 2) items.push("ellipsis");
  for (let i = start; i <= end; i++) items.push(i);
  if (end < pageCount - 1) items.push("ellipsis");
  items.push(pageCount);
  return items;
}

// A single cell inside the segmented control — no per-cell border; the wrapping
// pill carries the border so the group reads as one cohesive control.
const cellBase =
  "flex h-9 min-w-9 items-center justify-center rounded-lg text-sm font-medium transition-all duration-150";

function NumberedNav({
  page,
  pageCount,
  onPage,
}: {
  page: number;
  pageCount: number;
  onPage: (p: number) => void;
}) {
  if (pageCount <= 1) return null;

  const step = `${cellBase} gap-1 px-3.5 text-text/70 enabled:hover:bg-black/[0.05] enabled:hover:text-text enabled:active:scale-95 disabled:cursor-not-allowed disabled:opacity-30 dark:enabled:hover:bg-white/[0.06]`;

  return (
    <nav
      className="inline-flex items-center gap-1 rounded-xl border border-[var(--border)] bg-[var(--bg-elev)] p-1"
      style={{ boxShadow: "var(--shadow-card)" }}
      aria-label="Pagination"
    >
      <button
        type="button"
        onClick={() => onPage(page - 1)}
        disabled={page <= 1}
        aria-label="Previous page"
        className={step}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M15 18l-6-6 6-6" />
        </svg>
        Previous
      </button>

      <span className="mx-0.5 h-5 w-px bg-[var(--border)]" aria-hidden />

      {pageItems(page, pageCount).map((it, i) =>
        it === "ellipsis" ? (
          <span key={`ellipsis-${i}`} className="flex h-9 w-6 items-center justify-center text-sm text-text/30" aria-hidden>
            …
          </span>
        ) : (
          <button
            key={it}
            type="button"
            onClick={() => onPage(it)}
            aria-current={it === page ? "page" : undefined}
            className={`${cellBase} px-2.5 ${
              it === page
                ? "bg-primary text-white shadow-btn"
                : "text-text/70 hover:bg-black/[0.05] hover:text-text active:scale-95 dark:hover:bg-white/[0.06]"
            }`}
          >
            {it}
          </button>
        ),
      )}

      <span className="mx-0.5 h-5 w-px bg-[var(--border)]" aria-hidden />

      <button
        type="button"
        onClick={() => onPage(page + 1)}
        disabled={page >= pageCount}
        aria-label="Next page"
        className={step}
      >
        Next
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>
    </nav>
  );
}

// A single icon-only ‹ / › button that flanks the summary in the controls row
// (‹ before "1–20 of N", › after). Disables at its bound.
function StepButton({
  dir,
  page,
  pageCount,
  onPage,
  label,
}: {
  dir: -1 | 1;
  page: number;
  pageCount: number;
  onPage: (p: number) => void;
  label?: string; // when set, render a text button ("Prev"/"Next") instead of an icon-only ‹ / ›
}) {
  const disabled = dir < 0 ? page <= 1 : page >= pageCount;
  const base =
    "flex h-9 items-center justify-center rounded-lg border border-[var(--border)] text-text/70 transition-colors enabled:hover:border-primary/40 enabled:hover:bg-black/[0.05] enabled:active:scale-95 disabled:opacity-30 dark:enabled:hover:bg-white/[0.06]";
  return (
    <button
      type="button"
      onClick={() => onPage(page + dir)}
      disabled={disabled}
      aria-label={dir < 0 ? "Previous page" : "Next page"}
      className={label ? `${base} gap-1 px-3.5 text-sm font-medium` : `${base} w-9`}
    >
      {label ? (
        <>
          {dir < 0 && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>}
          {label}
          {dir > 0 && <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 18l6-6-6-6" /></svg>}
        </>
      ) : (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d={dir < 0 ? "M15 18l-6-6 6-6" : "M9 18l6-6-6-6"} />
        </svg>
      )}
    </button>
  );
}

export default function Pagination({
  page,
  pageSize,
  total,
  onPage,
  onPageSize,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  showSummary = false,
  showNav = true,
  showSteps = false,
  stepLabels = false,
}: PaginationProps) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const hasLeft = showSummary || !!onPageSize;
  const hasNav = showNav && pageCount > 1;

  // Nothing to show at all.
  if (!hasLeft && !hasNav) return null;

  const start = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  const steps = showSteps && pageCount > 1;

  const left = hasLeft && (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-sm">
      {/* Summary + step arrows are extra chrome on a small screen — hide them there and keep just
          the per-page selector, which stays visible at every size. */}
      {steps && <span className="hidden sm:inline-flex"><StepButton dir={-1} page={page} pageCount={pageCount} onPage={onPage} label={stepLabels ? "Prev" : undefined} /></span>}
      {showSummary && (
        <span className="hidden px-1 sm:inline" style={{ color: "var(--text-muted)" }}>
          <span className="font-semibold text-text/80">{start}</span>
          {end > start && <>–<span className="font-semibold text-text/80">{end}</span></>}{" "}
          of <span className="font-semibold text-text/80">{total}</span>
        </span>
      )}
      {steps && <span className="hidden sm:inline-flex"><StepButton dir={1} page={page} pageCount={pageCount} onPage={onPage} label={stepLabels ? "Next" : undefined} /></span>}
      {onPageSize && (
        <div className="flex items-center gap-2 sm:ml-2" style={{ color: "var(--text-muted)" }}>
          <span>Per page</span>
          <Dropdown
            value={String(pageSize)}
            onChange={(v) => onPageSize(Number(v))}
            options={pageSizeOptions.map((n) => ({ value: String(n), label: String(n) }))}
            ariaLabel="Items per page"
            align="right"
            className="w-20"
          />
        </div>
      )}
    </div>
  );

  // Controls-only bar (summary + per-page) sits on the right; a nav-only bar
  // (page numbers) centers; when both are present they split left/right.
  const justify = hasLeft && hasNav ? "sm:justify-between" : hasLeft ? "sm:justify-end" : "sm:justify-center";

  return (
    <div className={`flex flex-col gap-3 sm:flex-row sm:items-center ${justify}`}>
      {left}
      {hasNav && <NumberedNav page={page} pageCount={pageCount} onPage={onPage} />}
    </div>
  );
}
